import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import AccountService from "../AccountService";
import TutorSubsAdmin from "./TutorSubsAdmin";

const PRODUCT_LABELS = {
    jigsaw_pack: "Jigsaw pack",
    no_ads: "No Ads",
    tutor_standard: "Tutor",
    tutor_extra_pdf: "Tutor extra PDF",
    web_premium: "Web premium",
    premium: "Puzzle Premium",
    credit_pack: "Credits",
};

function whenLabel(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
}

function moneyLabel(cents, currency) {
    if (cents == null || cents === "") return "";
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return "";
    return `$${(amount / 100).toFixed(2)} ${String(currency || "USD").toUpperCase()}`;
}

function productLabel(row) {
    const key = row?.productKey || "";
    return PRODUCT_LABELS[key] || key || "Web purchase";
}

function itemLabel(row) {
    if (row?.packTitle) return row.packTitle;
    if (row?.packId) return row.packId;
    return row?.priceKey || "";
}

export default function WebStripePurchases({ userId, userDetails, onGranted }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [purchases, setPurchases] = useState([]);
    const [grants, setGrants] = useState([]);

    useEffect(() => {
        let cancel = false;
        if (!userId) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        setError("");
        AccountService.getWebPurchases(userId)
            .then((data) => {
                if (cancel) return;
                setPurchases(Array.isArray(data?.purchases) ? data.purchases : []);
                setGrants(Array.isArray(data?.jigsawGrants) ? data.jigsawGrants : []);
            })
            .catch((loadError) => {
                if (cancel) return;
                setPurchases([]);
                setGrants([]);
                setError(loadError?.message || "Could not load web purchases.");
            })
            .finally(() => {
                if (!cancel) setLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [userId]);

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Web purchases
            </Typography>
            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : purchases.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No web purchases recorded for this account.
                </Typography>
            ) : (
                <Table size="small" sx={{ mb: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {purchases.map((row) => (
                            <TableRow key={row.id || row.sessionId}>
                                <TableCell>{whenLabel(row.completedAt || row.createdAt)}</TableCell>
                                <TableCell>{productLabel(row)}</TableCell>
                                <TableCell>{itemLabel(row)}</TableCell>
                                <TableCell>{moneyLabel(row.amountTotal, row.currency)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={row.status || "unknown"}
                                        color={String(row.status || "").toLowerCase() === "complete"
                                            || String(row.status || "").toLowerCase() === "granted" ? "success" : "default"}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {grants.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Unlocked jigsaw packs
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Granted</TableCell>
                                <TableCell>Pack</TableCell>
                                <TableCell>Transaction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {grants.map((row) => (
                                <TableRow key={row.id || row.transactionId}>
                                    <TableCell>{whenLabel(row.grantedAt)}</TableCell>
                                    <TableCell>{row.packTitle || row.packId}</TableCell>
                                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                        {row.transactionId}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ) : null}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Tutor
            </Typography>
            <TutorSubsAdmin userId={userId} userDetails={userDetails} onGranted={onGranted} />
        </Box>
    );
}
